/**
 * The GPU side of the post stack: one MSAA HDR scene pass shared by two RenderPipelines (bloom tiers and the
 * no-bloom Low tier), the Eclipse bloom node, the procedural lens dirt and the composite (style guide §3.2,
 * §3.5, §3.8). Built once at load; `setPreset` only picks a pipeline and reconfigures the bloom chain.
 *
 * Composite, in order: radial chromatic aberration (hit kick only) → + bloom × strength × (1 + lens dirt)
 * → boost grade (saturation, exposure) → the renderer's output transform (ACES + the environment's exposure +
 * sRGB, via `renderOutput`) → vignette → damage edge → 8-bit dither.
 */
import {
  ClampToEdgeWrapping,
  DataTexture,
  LinearFilter,
  RedFormat,
  RenderPipeline,
  UnsignedByteType,
  Vector3,
  Vector4,
  type Node,
  type PerspectiveCamera,
  type Scene,
  type TextureNode,
  type UniformNode,
  type WebGPURenderer,
} from 'three/webgpu'
import {
  Fn,
  abs,
  dot,
  float,
  interleavedGradientNoise,
  length,
  luminance,
  max,
  min,
  mix,
  pass,
  pow,
  rand,
  renderOutput,
  screenCoordinate,
  smoothstep,
  step,
  texture,
  texture3D,
  uniform,
  uv,
  vec2,
  vec3,
  vec4,
} from 'three/tsl'
import { fxaa } from 'three/addons/tsl/display/FXAANode.js'
import { smaa } from 'three/addons/tsl/display/SMAANode.js'
import { atmosphereOf } from '../environment/atmosphere'
import { createAoStage, type AoSettings } from './ao'
import { GodRayNode, applyHaze, hazeUniforms, rayTint, sunScreenPosition, syncHaze } from './atmosphere'
import { createGradeTexture, gradeParams, toneMappingOf } from './grade'
import { EclipseBloomNode, tentSample } from './bloom'
import type { BloomMode, PostFile } from './data'
import { generateLensDirt } from './dirt'
import { webgl2CapsOf } from '../webgl2'

export type FloatUniform = UniformNode<'float', number>

/** Uniforms the controller writes (tuning, tier and per-frame feedback). */
export interface PostUniforms {
  readonly threshold: { value: number }
  readonly smoothWidth: { value: number }
  readonly spread: { value: number }
  readonly strength: { value: number }
  readonly dirt: { value: number }
  readonly vignette: { value: number }
  /**
   * Per-frame feedback packed in one vec4 (x = damage edge 0..1, y = chromatic aberration offset at the screen
   * edge in UV units, z = boost grade 0..1): writing Vector4 fields never boxes a double, while a float
   * UniformNode's `value` field is shared with object-valued uniforms and would allocate on every store.
   */
  readonly frame: { readonly value: { x: number; y: number; z: number; w: number } }
  readonly boostSaturation: { value: number }
  readonly boostExposure: { value: number }
  readonly boostVignette: { value: number }
  readonly dither: { value: number }
  /** Film grain amplitude in display units (0 = off). */
  readonly grain: { value: number }
  /** Constant radial chromatic aberration at the screen corners, UV units (lens character, High/Medium). */
  readonly caEdge: { value: number }
  /** Grade LUT blend (0 = tone map only). */
  readonly lut: { value: number }
}

/** Pass diagnostics for lookdev (never used by the game): one buffer shown alone, display-encoded. */
export type PostDebugView = 'ao' | 'normals' | 'nopost' | 'godrays' | 'raysource'
export const POST_DEBUG_VIEWS: readonly PostDebugView[] = ['ao', 'normals', 'nopost', 'godrays', 'raysource']

/** Atmosphere tier (atmosphere.ts): god-ray samples (0 = off) and resolution. */
export interface AtmosphereSettings {
  godRaySamples: number
  godRayScale: number
}

/** What the controller drives. The real one is `buildPostGraph`; tests use fakes. */
export interface PostTarget {
  readonly uniforms: PostUniforms
  /** Renders the frame through the bloom pipeline (High and Medium). */
  renderBloom(): void
  /** Renders the frame through the no-bloom pipeline (Low). */
  renderPlain(): void
  /** Bloom chain mode and level weights (finest first). Called on tier or tuning changes, never per frame. */
  configureBloom(mode: Exclude<BloomMode, 'off'>, weights: Float32Array, count: number): void
  /** Screen-space AO tier (ao.ts); samples 0 = off. Never per frame. */
  configureAo(settings: AoSettings): void
  /** God rays tier. Never per frame. */
  configureAtmosphere(settings: AtmosphereSettings): void
  /** Renders one diagnostic buffer to the canvas (lookdev). */
  renderDebug(view: PostDebugView): void
  setSize(width: number, height: number, pixelRatio: number): void
  dispose(): void
}

function srgbTriplet(hex: string): Vector3 {
  // Display-space colour: the composite applies it after the output transform, so it stays sRGB-encoded.
  const n = Number.parseInt(hex.slice(1), 16)
  return new Vector3(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255)
}

type AaMode = 'msaa' | PostFile['aa']['webgl2']

function isWebGPU(renderer: WebGPURenderer): boolean {
  return (renderer.backend as { isWebGPUBackend?: boolean } | undefined)?.isWebGPUBackend === true
}

function f(v: number): FloatUniform {
  return uniform(v)
}

/** 1×1 white: the AO texture while AO is off (the scene pass keeps one material variant on every preset). */
function whiteTexture(): DataTexture {
  const tex = new DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1)
  tex.name = 'post.white'
  tex.needsUpdate = true
  return tex
}

function lensDirtTexture(data: PostFile): DataTexture {
  const d = data.dirt
  const tex = new DataTexture(generateLensDirt(d), d.width, d.height, RedFormat, UnsignedByteType)
  tex.name = 'post.lensDirt'
  tex.minFilter = LinearFilter
  tex.magFilter = LinearFilter
  tex.wrapS = ClampToEdgeWrapping
  tex.wrapT = ClampToEdgeWrapping
  tex.generateMipmaps = false
  tex.needsUpdate = true
  return tex
}

export function buildPostGraph(renderer: WebGPURenderer, scene: Scene, camera: PerspectiveCamera, data: PostFile): PostTarget {
  const u = {
    threshold: f(data.bloom.threshold),
    smoothWidth: f(data.bloom.smoothWidth),
    spread: f(data.bloom.upsampleSpread),
    strength: f(data.bloom.strength),
    dirt: f(0),
    vignette: f(0),
    frame: uniform(new Vector4()),
    boostSaturation: f(data.feedback.boostSaturation),
    boostExposure: f(data.feedback.boostExposure),
    boostVignette: f(data.feedback.boostVignette),
    dither: f(data.dither),
    grain: f(0),
    caEdge: f(0),
    lut: f(data.grade.intensity),
  }
  const vigInner = f(data.vignette.inner)
  const vigOuter = f(data.vignette.outer)
  const edgeInner = f(data.feedback.edgeInner)
  const edgeOuter = f(data.feedback.edgeOuter)
  const edgeColor = uniform(srgbTriplet(data.feedback.edgeColor))
  const edgeKeep = f(data.feedback.edgeKeep)
  const aspect = f(1)
  const edgeAmount = u.frame.x
  const caOffset = u.frame.y
  const boost = u.frame.z
  const dirtAspect = data.dirt.width / data.dirt.height
  const dirtPeak = f(data.dirt.peak)

  // Anti-aliasing (measured, see docs/art/reviews/post.md): WebGPU keeps the renderer's 4× MSAA in the HDR scene
  // pass (resolved before bloom). WebGL2 cannot resolve a multisampled pass with the reversed-Z float depth
  // texture (the frame comes out black), so it renders the pass without MSAA and anti-aliases the display image.
  const webgpu = isWebGPU(renderer)
  // The WebGL2 fallback leaves out GTAO and the god rays (webgl2.json, D-053); WebGPU keeps everything.
  const caps = webgpu ? null : webgl2CapsOf(renderer)
  const aoAllowed = caps?.ao ?? true
  const raysAllowed = caps?.godRays ?? true
  const aa: AaMode = webgpu ? 'msaa' : data.aa.webgl2
  const scenePass = aa === 'msaa' ? pass(scene, camera) : pass(scene, camera, { samples: 0 })
  // AO on indirect light only: the prepass and GTAO render into a texture the scene pass reads through a context.
  const white = whiteTexture()
  const aoStage = createAoStage(renderer, scene, camera, white)
  scenePass.contextNode = aoStage.context as unknown as typeof scenePass.contextNode
  const sceneColor = scenePass.getTextureNode('output')
  const bloom = new EclipseBloomNode(sceneColor, { threshold: u.threshold, smoothWidth: u.smoothWidth, spread: u.spread }, data.bloom.karis)
  const bloomTex = bloom.getTextureNode()

  // Atmosphere (atmosphere.ts): haze reads the prepass depth (High/Medium); god rays run on every preset, with the
  // depth sky mask where the prepass exists and colour-only occlusion on Low.
  const atmos = atmosphereOf(scene)
  const haze = hazeUniforms(atmos, camera)
  const sky = atmos.sky
  const g = data.godRays
  const godU = {
    sun: uniform(new Vector3(0.5, 0.5, 0)),
    samples: f(0),
    decay: f(g.decay),
    length: f(g.length),
    falloff: f(g.falloff),
    threshold: f(g.threshold),
    clampMax: f(g.clampMax),
    corona: f(g.corona),
    aspect: f(1),
  }
  const raysFull = new GodRayNode(sceneColor, aoStage.depth, godU)
  const raysLow = new GodRayNode(sceneColor, null, godU)
  const raysIntensity = f(g.intensity)
  const raysMargin = g.margin
  const dirtTex = lensDirtTexture(data)
  const dirtNode: TextureNode = texture(dirtTex)

  // Tone mapping happens once, here (post.json `toneMapping`), with the environment's exposure
  // (renderer.toneMappingExposure) and the renderer's output colour space; the grade LUT follows in display space.
  const toneMapping = toneMappingOf(data.toneMapping)
  const colorSpace = renderer.outputColorSpace
  const lutSize = data.grade.lutSize
  const lutTex = createGradeTexture(lutSize, gradeParams(data.grade))
  const lutNode = texture3D(lutTex)
  const display = (withBloom: boolean): Node<'vec4'> =>
    Fn(() => {
      const p = uv()
      const centered = p.sub(0.5).mul(2)
      // Aspect-corrected radius: 1 = half the screen height.
      const r = length(centered.mul(vec2(aspect, 1)))
      // Radial chromatic aberration: a faint constant lens fringe toward the corners (caEdge, r² falloff) plus the
      // ≤ 0.3 s hit kick (caOffset).
      const r2 = dot(centered, centered).mul(0.5)
      const off = p.sub(0.5).mul(caOffset.add(u.caEdge.mul(r2)).mul(2))
      const hdr = vec3(sceneColor.sample(p.sub(off)).r, sceneColor.sample(p).g, sceneColor.sample(p.add(off)).b).toVar()
      // Atmosphere before bloom: depth fog, field dust, then the god rays (tinted by the star).
      if (withBloom) hdr.assign(applyHaze(hdr, p, aoStage.depth, haze, sky))
      // The ray targets are only redrawn while the star is visible: gate by the current visibility so a stale frame
      // never lingers once the star leaves the screen.
      if (raysAllowed) {
        const rays = (withBloom ? raysFull : raysLow).getTextureNode().sample(p).rgb
        // Over near geometry the shafts are damped so backlit silhouettes stay dark (prepass depth; Low has none).
        const overSky = withBloom ? mix(float(0.4), float(1), step(aoStage.depth.sample(p).r, float(0))) : float(1)
        hdr.addAssign(rayTint(rays, haze.sunColor).mul(raysIntensity).mul(godU.sun.z).mul(overSky))
      }
      if (withBloom) {
        const b = tentSample(bloomTex, p, bloom.outputTexel)
        // Lens dirt: "cover" fit so smudges stay round on any aspect ratio.
        const fit = vec2(min(aspect.div(dirtAspect), 1), min(float(dirtAspect).div(aspect), 1))
        const dirtUv = p.sub(0.5).mul(fit).add(0.5)
        const dirt = dirtNode.sample(dirtUv).r.mul(dirtPeak).mul(u.dirt)
        hdr.addAssign(b.mul(u.strength).mul(dirt.add(1)))
      }
      // Boost grade: a touch more saturation and light while boosting (linear, pre-tonemap).
      const lum = luminance(hdr)
      const graded = max(mix(vec3(lum), hdr, boost.mul(u.boostSaturation).add(1)).mul(boost.mul(u.boostExposure).add(1)), vec3(0))
      // Tone map once (+ the environment's exposure) and encode sRGB, then the display-domain grade LUT.
      const out = renderOutput(vec4(graded, 1), toneMapping, colorSpace).rgb.toVar()
      const uvw = out.mul(1 - 1 / lutSize).add(0.5 / lutSize)
      out.assign(mix(out, lutNode.sample(uvw).rgb, u.lut))
      const vig = u.vignette.add(boost.mul(u.boostVignette))
      out.mulAssign(float(1).sub(vig.mul(smoothstep(vigInner, vigOuter, r))))
      // Damage edge: a superellipse that hugs the screen border (1 at the edge midpoints).
      const a4 = pow(abs(centered), vec2(4))
      const edgeR = pow(a4.x.add(a4.y), 0.25)
      out.assign(mix(out, out.mul(edgeKeep).add(edgeColor), smoothstep(edgeInner, edgeOuter, edgeR).mul(edgeAmount)))
      return vec4(out, 1)
    })()

  const composite = (withBloom: boolean): Node<'vec4'> => {
    const img = display(withBloom)
    const aaImg = (aa === 'smaa' ? smaa(img) : aa === 'fxaa' ? fxaa(img) : img) as Node<'vec4'>
    // Film grain after AA (grain is never anti-aliased): triangular per-pixel noise, re-seeded every frame, strongest
    // in the mid-tones and absent from clipped highlights and pure black.
    const seed = screenCoordinate.xy.add(vec2(u.frame.w.mul(17.31), u.frame.w.mul(29.17)))
    const noise = rand(seed).add(rand(seed.add(vec2(3.7, 11.3)))).sub(1)
    const lum = luminance(aaImg.rgb)
    const grainWeight = smoothstep(0, 0.12, lum).mul(float(1).sub(smoothstep(0.75, 1, lum)))
    const grained = aaImg.rgb.add(noise.mul(u.grain).mul(grainWeight))
    // Dither last, to kill 8-bit banding in the dark sky, bloom falloff and vignette.
    return vec4(grained.add(interleavedGradientNoise(screenCoordinate).sub(0.5).mul(u.dither.div(255))), 1)
  }

  const makePipeline = (withBloom: boolean): RenderPipeline => {
    const rp = new RenderPipeline(renderer)
    rp.outputColorTransform = false
    rp.outputNode = composite(withBloom)
    return rp
  }
  const bloomPipeline = makePipeline(true)
  const plainPipeline = makePipeline(false)

  // Per frame, before rendering: the star's screen position (rays) and the key colour (haze). Allocation-free.
  const updateAtmosphere = (): void => {
    camera.updateMatrixWorld()
    syncHaze(haze, atmos)
    sunScreenPosition(atmos.sunDirection, camera, raysMargin, godU.sun.value)
  }

  // Diagnostics, built on first use.
  const debugPipelines = new Map<PostDebugView, RenderPipeline>()
  const debugNode = (view: PostDebugView): Node<'vec4'> => {
    if (view === 'ao') return vec4(vec3(aoStage.aoTexture.sample(uv()).r), 1)
    if (view === 'normals') return vec4(aoStage.normals.sample(uv()).xyz.mul(0.5).add(0.5), 1)
    if (view === 'raysource') return vec4(renderOutput(vec4(raysFull.getMaskNode().sample(uv()).rgb, 1), toneMapping, colorSpace).rgb, 1)
    if (view === 'godrays') return vec4(renderOutput(vec4(raysFull.getTextureNode().sample(uv()).rgb.mul(raysIntensity), 1), toneMapping, colorSpace).rgb, 1)
    return vec4(renderOutput(vec4(sceneColor.sample(uv()).rgb, 1), toneMapping, colorSpace).rgb, 1)
  }
  const debugPipeline = (view: PostDebugView): RenderPipeline => {
    let rp = debugPipelines.get(view)
    if (!rp) {
      rp = new RenderPipeline(renderer)
      rp.outputColorTransform = false
      rp.outputNode = debugNode(view)
      debugPipelines.set(view, rp)
    }
    return rp
  }

  return {
    uniforms: u,
    renderBloom(): void {
      updateAtmosphere()
      aoStage.render()
      bloomPipeline.render()
    },
    renderPlain(): void {
      updateAtmosphere()
      aoStage.render()
      plainPipeline.render()
    },
    configureAtmosphere(s: AtmosphereSettings): void {
      const on = raysAllowed && s.godRaySamples > 0
      godU.samples.value = on ? s.godRaySamples : 0
      raysFull.enabled = on
      raysLow.enabled = on
      raysFull.scale = s.godRayScale
      raysLow.scale = s.godRayScale
    },
    configureAo(settings: AoSettings): void {
      aoStage.configure(aoAllowed ? settings : { ...settings, samples: 0 })
    },
    renderDebug(view: PostDebugView): void {
      updateAtmosphere()
      aoStage.render()
      debugPipeline(view).render()
    },
    configureBloom(mode: Exclude<BloomMode, 'off'>, weights: Float32Array, count: number): void {
      bloom.configure(mode, weights, count)
    },
    setSize(width: number, height: number, pixelRatio: number): void {
      renderer.setPixelRatio(pixelRatio)
      renderer.setSize(width, height, false)
      aspect.value = height > 0 ? width / height : 1
      godU.aspect.value = aspect.value
    },
    dispose(): void {
      bloomPipeline.dispose()
      plainPipeline.dispose()
      for (const rp of debugPipelines.values()) rp.dispose()
      bloom.dispose()
      aoStage.dispose()
      raysFull.dispose()
      raysLow.dispose()
      scenePass.dispose()
      dirtTex.dispose()
      white.dispose()
      lutTex.dispose()
    },
  }
}

